// src/utils/profileUtils.ts

import { Profile } from '../types/profile';

/**
 * Check if a user's profile is complete enough to use the app
 */
export function isProfileComplete(profile: Profile | null): boolean {
  if (!profile) return false;

  // Required fields for profile completion
  const hasDisplayName = !!profile.display_name?.trim();
  const hasPlayPreferences = Array.isArray(profile.play_preferences) &&
                             profile.play_preferences.length > 0;

  // Location is only required if user plays in-person
  const playPreferences = profile.play_preferences || [];
  const onlyOnline = playPreferences.length === 1 && playPreferences[0] === 'online';
  const hasLocation = !!profile.location_city &&
                      profile.location_lat != null &&
                      profile.location_lng != null;

  // If only playing online, location is optional
  const locationRequirement = onlyOnline ? true : hasLocation;

  return hasDisplayName && hasPlayPreferences && locationRequirement;
}

/**
 * Get a message explaining what's missing from the profile
 */
export function getProfileIncompleteMessage(profile: Profile | null): string {
  if (!profile) return 'Please complete your profile to start using Dice Cat.';

  const missing: string[] = [];

  if (!profile.display_name?.trim()) {
    missing.push('display name');
  }

  if (!Array.isArray(profile.play_preferences) || profile.play_preferences.length === 0) {
    missing.push('play preferences');
  }

  // Location is only required if user plays in-person
  const playPreferences = profile.play_preferences || [];
  const onlyOnline = playPreferences.length === 1 && playPreferences[0] === 'online';
  const hasLocation = !!profile.location_city && profile.location_lat != null && profile.location_lng != null;

  if (!onlyOnline && !hasLocation) {
    missing.push('location');
  }

  if (missing.length === 0) {
    return 'Your profile is complete!';
  }

  return `Please add your ${missing.join(', ')} to start using Dice Cat.`;
}